import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Popover, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

const SpinWheelPicker = ({ 
  values, 
  value, 
  onChange, 
  open, 
  anchorEl, 
  onClose,
  itemHeight = 40,
  visibleItems = 5
}) => {
  const listRef = useRef(null);
  const [highlightedValue, setHighlightedValue] = useState(value);

  // Sync internal state when opening or prop changes
  useEffect(() => {
    if (open) {
      setHighlightedValue(value);
    }
  }, [open, value]);

  // Initial scroll positioning
  useEffect(() => {
    if (open && values && values.length > 0) {
      const timer = setTimeout(() => {
        if (listRef.current) {
          // Find nearest value index
          const matchIndex = values.reduce((bestIdx, curr, currIdx) => {
             const bestVal = values[bestIdx];
             return Math.abs(curr - value) < Math.abs(bestVal - value) ? currIdx : bestIdx;
          }, 0);
          
          setHighlightedValue(values[matchIndex]);

          // Scroll directly to position. 
          // Since padding-top equals half visible height, scrollTop = index * itemHeight aligns that item to center.
          listRef.current.scrollTop = matchIndex * itemHeight;
        }
      }, 0); // Immediate execution after render
      return () => clearTimeout(timer);
    }
  }, [open, values, value, itemHeight]);

  const handleScroll = (e) => {
    const scrollTop = e.target.scrollTop;
    const index = Math.round(scrollTop / itemHeight);
    const safeIndex = Math.min(Math.max(0, index), values.length - 1);
    
    const newVal = values[safeIndex];
    if (newVal !== highlightedValue) {
        setHighlightedValue(newVal);
    }
  };

  const handleItemClick = (val) => {
    onChange(val);
    onClose();
  };

  // Commit value on closing (clicking backdrop)
  const handlePopoverClose = () => {
    if (highlightedValue !== undefined && highlightedValue !== null) {
        onChange(highlightedValue);
    }
    onClose();
  };

  if (!values || values.length === 0) return null;

  const halfHeight = itemHeight * Math.floor(visibleItems / 2);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handlePopoverClose}
      anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
      transformOrigin={{ vertical: 'center', horizontal: 'center' }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: 8,
          }
        }
      }}
    >
      <Box
        sx={{
          position: 'relative',
          height: itemHeight * visibleItems,
          width: 90,
          overflow: 'hidden',
          bgcolor: 'background.paper'
        }}
      >
        {/* Selection Highlight / Center Bar */}
        <Box
          sx={{
            position: 'absolute',
            top: halfHeight,
            left: 0,
            right: 0,
            height: itemHeight,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
            borderTop: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            borderBottom: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />

        {/* Top Fade Gradient */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: halfHeight,
            background: (theme) => `linear-gradient(to bottom, ${theme.palette.background.paper} 30%, transparent 100%)`,
            pointerEvents: 'none',
            zIndex: 2,
          }}
        />

        {/* Bottom Fade Gradient */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: halfHeight,
            background: (theme) => `linear-gradient(to top, ${theme.palette.background.paper} 30%, transparent 100%)`,
            pointerEvents: 'none',
            zIndex: 2,
          }}
        />

        {/* Scrollable Content */}
        <Box
          ref={listRef}
          onScroll={handleScroll}
          sx={{
            height: '100%',
            overflowY: 'auto',
            scrollSnapType: 'y mandatory',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
            py: `${halfHeight}px`,
          }}
        >
          {values.map((val) => {
            const isSelected = val === highlightedValue;
            return (
                <Box
                key={val}
                data-value={val}
                onClick={() => handleItemClick(val)}
                sx={{
                    height: itemHeight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    scrollSnapAlign: 'center',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    WebkitTouchCallout: 'none',
                }}
                >
                <Typography
                    sx={{
                    fontSize: isSelected ? '1.2rem' : '0.9rem',
                    fontWeight: isSelected ? 'bold' : 'normal',
                    color: isSelected ? 'primary.main' : 'text.secondary',
                    opacity: isSelected ? 1 : 0.5,
                    transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                    transition: 'all 0.15s ease-out',
                    }}
                >
                    {val}
                </Typography>
                </Box>
            );
          })}
        </Box>
      </Box>
    </Popover>
  );
};

SpinWheelPicker.propTypes = {
  values: PropTypes.array.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.any,
  onClose: PropTypes.func.isRequired,
  itemHeight: PropTypes.number,
  visibleItems: PropTypes.number
};

export default SpinWheelPicker;